<dialog-header
  title="Developer API"
  [image]="data.integration.image"
  (onClose)="dialogRef.close()"
></dialog-header>

<div
  mat-dialog-content
  class="api-dialog-content"
  style="
    padding-bottom: 20px;
    border-top: 1px solid lightgray;
    border-bottom: 1px solid lightgray;
    background-color: var(--app-container);
  "
>
  <mat-tab-group
    mat-align-tabs="start"
    mat-stretch-tabs="false"
    disableRipple="true"
    [animationDuration]="0"
    [selectedIndex]="1"
    (selectedTabChange)="tabChanged($event.index)"
  >
    <mat-tab label="Overview">
      <div class="form-field tab-content">
        <p class="dialog-body">
          Connect your own applications and third-party services to Remy using
          scoped API keys. Each key can be limited to only the data and actions
          that integration needs.
        </p>

        <link-text text="View API documentation" [url]="docsUrl"></link-text>

        <div class="detail-container overview-detail">
          <div class="form-field-label-title">Secure by default</div>
          <p class="dialog-body">
            API key secrets are shown once and stored as one-way hashes. Use a
            separate key for each integration and revoke a key as soon as it is
            no longer needed.
          </p>

          <div class="form-field-label-title overview-section-title">
            Scoped access
          </div>
          <p class="dialog-body">
            Read, write, redemption and voucher permissions are managed
            independently, so integrations only receive the access you select.
          </p>
        </div>
      </div>
    </mat-tab>

    <mat-tab label="API Keys">
      <div class="tab-content">
        <section class="secret-panel" *ngIf="revealedSecret">
          <div class="form-field-label-title">
            Copy {{ revealedKeyName }} now
          </div>
          <p class="form-field-label-subtitle">
            This key is shown only once. Remy cannot display it again after you
            dismiss this message.
          </p>
          <code class="secret-value">{{ revealedSecret }}</code>
          <div class="inline-actions">
            <app-primary-button
              title="Copy key"
              icon="content_copy"
              color="black"
              border="1px solid var(--app-text-grey)"
              (tapped)="copySecret()"
            ></app-primary-button>
            <app-primary-button
              title="I've saved it"
              bgColor="var(--app-timber)"
              color="white"
              (tapped)="dismissSecret()"
            ></app-primary-button>
          </div>
        </section>

        <section class="key-form" *ngIf="showForm">
          <div class="form-field-label-title">
            {{ editingKey ? "Edit API Key" : "Create API Key" }}
          </div>

          <div class="form-grid">
            <div class="form-field">
              <div class="form-field-label">
                Key Name<span style="color: red">*</span>
              </div>
              <input
                class="form-input"
                autocomplete="off"
                maxlength="80"
                [(ngModel)]="name"
                placeholder="e.g. Production website"
              />
            </div>

            <div class="form-field">
              <div class="form-field-label">Expiry Date (Optional)</div>
              <input class="form-input" type="date" [(ngModel)]="expiresAt" />
            </div>
          </div>

          <div class="scope-heading">
            <div>
              <div class="form-field-label-title">Scopes</div>
              <div class="form-field-label-subtitle">
                Grant only the access this integration needs.
              </div>
            </div>
            <div class="selected-count">{{ selectedScopes.size }} selected</div>
          </div>

          <div class="scope-grid">
            <label class="scope-option" *ngFor="let scope of scopeOptions">
              <mat-checkbox
                [checked]="selectedScopes.has(scope.value)"
                (change)="toggleScope(scope.value, $event.checked)"
              ></mat-checkbox>
              <span class="scope-copy">
                <span class="scope-label">{{ scope.label }}</span>
                <span class="scope-description">{{ scope.description }}</span>
                <code>{{ scope.value }}</code>
              </span>
            </label>
          </div>

          <div class="form-actions">
            <app-secondary-button
              title="Cancel"
              [disabled]="saving"
              (tapped)="cancelForm()"
            ></app-secondary-button>
            <app-primary-button
              [title]="editingKey ? 'Save Changes' : 'Create Key'"
              [disabled]="saving"
              bgColor="var(--app-timber)"
              color="white"
              (tapped)="save()"
            ></app-primary-button>
          </div>
        </section>

        <div class="keys-heading">
          <div>
            <div class="form-field-label-title">API Keys</div>
          </div>
          <app-primary-button
            *ngIf="!showForm"
            title="Create Key"
            icon="add"
            bgColor="var(--app-timber)"
            color="white"
            (tapped)="startCreate()"
          ></app-primary-button>
        </div>

        <div class="add-container empty-state" *ngIf="!loading && !keys.length">
          <div>
            <div class="form-field-label-title">No API Keys Yet</div>
            <div class="form-field-label-subtitle">
              Create a key to start making requests to the Remy public API.
            </div>
          </div>
        </div>

        <article
          class="social-container key-card"
          *ngFor="let key of keys"
          [class.inactive]="key.status !== 'active'"
        >
          <div class="key-main">
            <div class="key-title-row">
              <div class="form-field-label-title key-name">{{ key.name }}</div>
              <span class="status" [class]="'status ' + key.status">
                {{ key.status }}
              </span>
            </div>
            <code class="key-mask">
              {{ key.keyPrefix }}••••••••{{ key.lastFour }}
            </code>
            <div class="meta">
              <span>Created {{ key.createdAt | date: "mediumDate" }}</span>
              <span>
                Last used
                {{
                  key.lastUsedAt ? (key.lastUsedAt | date: "medium") : "never"
                }}
              </span>
              <span *ngIf="key.expiresAt">
                Expires {{ key.expiresAt | date: "mediumDate" }}
              </span>
            </div>
            <div class="scope-chips">
              <code *ngFor="let scope of key.scopes">{{ scope }}</code>
            </div>
          </div>
          <div class="key-actions" *ngIf="key.status === 'active'">
            <button
              mat-icon-button
              class="key-action-button"
              type="button"
              matTooltip="Edit API key"
              (click)="startEdit(key)"
            >
              <mat-icon>edit</mat-icon>
            </button>
            <button
              mat-icon-button
              class="key-action-button"
              type="button"
              matTooltip="Rotate API key"
              (click)="rotate(key)"
            >
              <mat-icon>autorenew</mat-icon>
            </button>
            <button
              mat-icon-button
              class="key-action-button danger"
              type="button"
              matTooltip="Revoke API key"
              (click)="revoke(key)"
            >
              <mat-icon>delete_outline</mat-icon>
            </button>
          </div>
        </article>
      </div>
    </mat-tab>

    <mat-tab label="Order Processing">
      <div class="tab-content order-settings-content">
        <p class="dialog-body">
          Configure how orders sent to the Developer API affect customer
          loyalty. These settings are independent from Zapier and other
          integrations.
        </p>

        <section class="detail-container order-settings-panel">
          <div class="setting-row">
            <div>
              <div class="form-field-label-title">
                Award stamps or points
              </div>
              <div class="form-field-label-subtitle">
                Apply the configurations below when an order is processed.
              </div>
            </div>
            <mat-slide-toggle
              [checked]="awardStampsApi"
              (change)="awardStampsApi = $event.checked"
            ></mat-slide-toggle>
          </div>

          <div class="setting-row">
            <div>
              <div class="form-field-label-title">Invite new customers</div>
              <div class="form-field-label-subtitle">
                Email customers who do not yet have a Remy account.
              </div>
            </div>
            <mat-slide-toggle
              [checked]="inviteUsersApi"
              (change)="inviteUsersApi = $event.checked"
            ></mat-slide-toggle>
          </div>

          <div class="setting-row">
            <div>
              <div class="form-field-label-title">
                Redeem reward coupon codes
              </div>
              <div class="form-field-label-subtitle">
                Automatically redeem a matching customer reward when an order
                includes a coupon code.
              </div>
            </div>
            <mat-slide-toggle
              [checked]="rewardAutomationApi"
              (change)="rewardAutomationApi = $event.checked"
            ></mat-slide-toggle>
          </div>
        </section>

        <div class="configuration-heading">
          <div>
            <div class="form-field-label-title">Order configurations</div>
            <div class="form-field-label-subtitle">
              Define the qualifying spend and loyalty card award.
            </div>
          </div>
          <app-primary-button
            title="Add Configuration"
            icon="add"
            color="black"
            border="1px solid var(--app-text-grey)"
            (tapped)="addConfiguration()"
          ></app-primary-button>
        </div>

        <div
          class="add-container empty-state"
          *ngIf="!apiConfig?.configurations?.length"
        >
          <div>
            <div class="form-field-label-title">No Configurations Yet</div>
            <div class="form-field-label-subtitle">
              Add one before enabling order awards.
            </div>
          </div>
        </div>

        <article
          class="social-container configuration-card"
          *ngFor="let configuration of apiConfig?.configurations"
        >
          <div>
            <div class="form-field-label-title">
              {{ rowLabel(configuration) }}
            </div>
            <div class="form-field-label-subtitle">
              Loyalty card configuration
            </div>
          </div>
          <div class="key-actions">
            <button
              mat-icon-button
              class="key-action-button"
              type="button"
              matTooltip="Edit configuration"
              (click)="editConfiguration(configuration)"
            >
              <mat-icon>edit</mat-icon>
            </button>
            <button
              mat-icon-button
              class="key-action-button danger"
              type="button"
              matTooltip="Remove configuration"
              (click)="deleteConfiguration(configuration._id!)"
            >
              <mat-icon>delete_outline</mat-icon>
            </button>
          </div>
        </article>

        <div class="form-actions order-settings-actions">
          <app-primary-button
            title="Save Order Settings"
            [disabled]="saving"
            bgColor="var(--app-timber)"
            color="white"
            (tapped)="saveOrderSettings()"
          ></app-primary-button>
        </div>
      </div>
    </mat-tab>

    <mat-tab label="Usage">
      <div class="tab-content usage-content">
        <div class="usage-toolbar">
          <div>
            <div class="form-field-label-title">API Usage</div>
            <div class="form-field-label-subtitle">
              Requests made with merchant-managed API keys.
            </div>
          </div>
          <div class="usage-toolbar-actions">
            <select
              class="form-input usage-period"
              [ngModel]="usageDays"
              (ngModelChange)="changeUsagePeriod($event)"
            >
              <option [ngValue]="7">Last 7 days</option>
              <option [ngValue]="30">Last 30 days</option>
              <option [ngValue]="90">Last 90 days</option>
            </select>
            <app-primary-button
              title="Export CSV"
              icon="download"
              color="black"
              border="1px solid var(--app-text-grey)"
              [disabled]="!usage || usageLoading"
              (tapped)="exportUsage()"
            ></app-primary-button>
          </div>
        </div>

        <div class="usage-summary" *ngIf="usage">
          <div class="detail-container usage-stat">
            <div class="usage-stat-value">{{ usage.totals.requests | number }}</div>
            <div class="form-field-label-subtitle">Requests</div>
          </div>
          <div class="detail-container usage-stat">
            <div class="usage-stat-value">{{ usage.totals.errors | number }}</div>
            <div class="form-field-label-subtitle">
              Errors ({{ usage.totals.errorRate | number: '1.0-2' }}%)
            </div>
          </div>
          <div class="detail-container usage-stat">
            <div class="usage-stat-value">
              {{ usage.totals.averageDurationMs | number }} ms
            </div>
            <div class="form-field-label-subtitle">Average response</div>
          </div>
        </div>

        <div
          class="add-container empty-state"
          *ngIf="!usageLoading && usage && !usage.totals.requests"
        >
          <div>
            <div class="form-field-label-title">No Usage Yet</div>
            <div class="form-field-label-subtitle">
              Requests made with your API keys will appear here.
            </div>
          </div>
        </div>

        <section
          class="social-container usage-key-card"
          *ngFor="let key of usage?.keys"
        >
          <div class="usage-key-heading">
            <div>
              <div class="key-title-row">
                <div class="form-field-label-title key-name">{{ key.name }}</div>
                <span class="status" [class]="'status ' + key.status">
                  {{ key.status }}
                </span>
              </div>
              <code class="key-mask">{{ key.keyPrefix }}…{{ key.lastFour }}</code>
            </div>
            <div class="usage-key-totals">
              <strong>{{ key.requests | number }}</strong> requests
              <span>•</span>
              <strong>{{ key.errors | number }}</strong> errors
            </div>
          </div>

          <div class="endpoint-table" *ngIf="key.endpoints.length">
            <div class="endpoint-row endpoint-header">
              <span>Endpoint</span>
              <span>Requests</span>
              <span>Errors</span>
              <span>Average</span>
            </div>
            <div class="endpoint-row" *ngFor="let endpoint of key.endpoints">
              <span class="endpoint-name">
                <code class="method">{{ endpoint.method }}</code>
                <code>{{ endpoint.route }}</code>
              </span>
              <span>{{ endpoint.requests | number }}</span>
              <span>{{ endpoint.errors | number }}</span>
              <span>{{ endpoint.averageDurationMs | number }} ms</span>
            </div>
          </div>

          <div class="form-field-label-subtitle no-key-usage" *ngIf="!key.endpoints.length">
            No requests in this period.
          </div>
        </section>
      </div>
    </mat-tab>
  </mat-tab-group>
</div>

<div mat-dialog-actions align="end">
  <app-primary-button
    title="Done"
    bgColor="var(--app-timber)"
    color="white"
    (tapped)="dialogRef.close()"
  ></app-primary-button>
</div>

<loader [loading]="loading || saving || usageLoading"></loader>
